<!-- eslint-disable max-len -->
<template>
  <div class="table-wrapper">
    <div class="table-content">
      <el-table
        ref="multipleTable"
        :row-key="rowKey"
        :default-sort="sorting"
        :data="tableRows"
        :empty-text="emptyText"
        :height="height"
        :cell-class-name="tableCellClassName"
        :row-class-name="tableRowClassName"
        border
        @selection-change="handleSelectionChange"
        @report-selection-change="handleReportSelectionChange"
        @sort-change="sortChange"
      >
        <slot name="beforeColumns" />
        <el-table-column
          v-if="enableSelection"
          type="selection"
        />
        <el-table-column
          v-for="(column, index) in columns"
          :key="index"
          :prop="index"
          :label="$t(`filters.${column.label}`)"
          :sortable="column.sortable"
          :align="column.align"
          :min-width="column.minWidth ? column.minWidth : '50'"
          :resizable="true"
        >
          <template
            slot-scope="scope"
          >
            <span v-if="column.type==='text'">{{ propertyValue(scope.row, column.key) }}</span>
            <span v-if="column.type==='stacked-text'">
              <template v-if="propertyValue(scope.row, column.key)">
                <div
                  v-for="(item, itemIndex) in propertyValue(scope.row, column.key).split(' ').filter(i => i)"
                  :key="itemIndex"
                >
                  {{ item }}
                </div>
              </template>
              <template v-else>
                -
              </template>
            </span>
            <!-- Just for displaying paymentType while testing -->
            <!-- <span v-if="column.type==='text'">{{ scope.row.paymentType + " " +
            propertyValue(scope.row, column.key) }}</span> -->
            <span v-if="column.type==='translatedText'">
              {{ $t(`filters.${propertyValue(scope.row, column.key)}`) }}
            </span>
            <span v-if="column.type === 'boolean'">
              <!-- <el-tag
                v-if="scope.row[index]"
                type="success"
              >√</el-tag>
              <el-tag
                v-else
                type="danger"
              >x</el-tag> -->
              <i
                v-if="scope.row[index]"
                class="state-icon fas fa-check-circle"
              />
              <i
                v-else
                class="state-icon fas fa-do-not-enter"
              />
            </span>
            <span v-if="column.type==='date'">
              {{ propertyValue(scope.row, column.key) | date('DD MMMM YYYY') }}
            </span>
            <span v-if="column.type==='stacked-dates'">
              <template v-if="propertyValue(scope.row, column.key)">
                <div
                  v-for="(dateStr, dateIndex) in propertyValue(scope.row, column.key).split(' ').filter(d => d)"
                  :key="dateIndex"
                >
                  {{ dateStr | date('DD MMMM YYYY') }}
                </div>
              </template>
              <template v-else-if="column.fallbackKey && propertyValue(scope.row, column.fallbackKey)">
                {{ propertyValue(scope.row, column.fallbackKey) | date('DD MMMM YYYY') }}
              </template>
              <template v-else>
                -
              </template>
            </span>
            <span v-if="column.type==='enum'">
              {{ $t(`enums.${column.enum}.${propertyValue(scope.row, column.key)}`) }}
            </span>
            <span v-if="column.type==='encoded'">
              {{
                $t(`enums.${column.enum}.${column.decode(propertyValue(scope.row, column.key))}`)
              }}
            </span>
            <span v-if="column.type==='function' && !column.enum">
              {{ column.get(propertyValue(scope.row, column.key), scope.row) }}
            </span>
            <span v-if="column.type==='function' && column.enum">
              {{
                $t(`enums.${column.enum}.${
                  column.get(propertyValue(scope.row, column.key), scope.row)
                }`)
              }}
            </span>
            <span
              v-if="column.type==='slot-collapse-list'"
              class="slot-collapse-list"
            >
              <el-collapse
                accordion
              >
                <el-collapse-item
                  :title="$t(column.collapseTitle)"
                  name="slot-collapse-item"
                >
                  <ul>
                    <li
                      v-for="(el, collapseIndex) in scope.row[column.collapseKey]"
                      :key="`${column.collapseKey}-${collapseIndex}`"
                    >
                      <el-button
                        v-if="(scope.row.status === SAMPLE_STATUSES.DRAFT &&
                          el[column.collapseAddon] === SAMPLE_REPORTS_STATUSES.draft &&
                          el[column.collapseValue]) ||
                          el[column.collapseAddon] !== SAMPLE_REPORTS_STATUSES.draft"
                        size="mini"
                        type="text"
                        @click.prevent="$emit('collapseItemClick', scope.row, el)"
                      >
                        <i
                          :class="`state-icon fas fa-${column.collapseItemIcon}`"
                        />
                        {{ $t(`collapse.${collapseItemLabelKey(column, el)}`) }}
                      </el-button>
                    </li>
                  </ul>
                </el-collapse-item>
              </el-collapse>
            </span>
            <TableActions
              v-else-if="column.type === 'actions'"
              :has-permissions="hasPermissions"
              :configuration="column"
              :row="scope.row"
              @actionClick="$emit($event, scope.row)"
            />
          </template>
          <template
            v-if="column.help"
            slot="header"
          >
            {{ $t(`filters.${column.label}`) }}
            <info-tooltip :tooltip-content="$t(`tooltips.${column.help}`)" />
          </template>
        </el-table-column>
        <el-table-column
          v-if="enableReportSelection"
          width="60"
        >
          <template slot="header">
            <i
              class="fas fa-file select-all-rdp"
              @click="toggleSelectAllReports"
            />
          </template>
          <template slot-scope="rdp">
            <el-checkbox
              v-if="rdp.row['reports']"
              :value="isReportRowSelected(rdp.row)"
              @change="(checked) => handleReportSelectionChange(rdp.row, checked)"
            />
          </template>
        </el-table-column>
        <slot name="afterColumns" />
      </el-table>
    </div>
    <el-row
      :gutter="20"
      type="flex"
      align="bottom"
      class="pagination-content"
    >
      <el-col
        v-if="enableSelection && bulkDisable"
        :span="8"
      >
        <div class="massive-action-disable">
          <div>{{ $t('tableButtons.disableSelected') }}</div>
          <el-button
            class="disable-selected"
            type="primary"
            @click="$emit('bulkDisable', multipleSelection)"
          >
            {{ $t('global.apply') }}
          </el-button>
        </div>
      </el-col>
      <el-col
        v-if="enableSelection && massiveActionsOptions.length"
        :span="8"
      >
        <div class="massive-actions">
          <label class="massive-actions__label">
            {{ $t('table.massiveActions.title') }}
          </label>
          <div class="massive-actions__select">
            <el-select
              v-model="massiveActionsValue"
              :disabled="!multipleSelection.length"
              :placeholder="$t('global.select')"
              size="mini"
            >
              <el-option
                v-for="item in massiveActionsOptions"
                :key="item.value"
                :label="$t(item.label)"
                :value="item.value"
                :disabled="item.disabled"
              />
            </el-select>
            <el-button
              :disabled="massiveActionsValue == null ||
                massiveActionsValue === '' || !multipleSelection.length"
              type="primary"
              size="mini"
              @click="$emit('massiveAction', massiveActionsValue, multipleSelection)"
            >
              {{ $t('table.massiveActions.submit') }}
            </el-button>
          </div>
        </div>
      </el-col>
      <el-col
        :span="24"
      >
        <el-pagination
          class="pagination"
          :current-page="currentPage"
          :page-sizes="[5, 10, 50, 100]"
          :page-size="perPage"
          background
          layout="sizes, prev, pager, next"
          :total="count"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </el-col>
      <el-col
        v-if="enableReportSelection"
        :span="2"
      >
        <div class="massive-action-disable">
          <el-button
            :disabled="!multipleReportSelection.length"
            class="disable-selected"
            type="primary"
            size="mini"
            @click="$emit('bulkDownloadRdp', multipleReportSelection)"
          >
            {{ $t('global.downloadPdf') }}
          </el-button>
        </div>
      </el-col>
    </el-row>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex';
import lodashGet from 'lodash.get';
import TableActions from '@/components/TableActions.vue';
import InfoTooltip from '@/components/InfoTooltip.vue';
import acl from '../services/acl';
import { SAMPLE_STATUSES, SAMPLE_REPORTS_STATUSES } from '../config/constants';


export default {
  name: 'TableWrapper',
  components: {
    TableActions,
    InfoTooltip,
  },
  props: {
    tableColumns: {
      type: Object,
      default: () => {},
    },
    tableKey: {
      type: String,
      required: true,
    },
    enableSelection: {
      type: Boolean,
      default: false,
    },
    enableReportSelection: {
      type: Boolean,
      default: false,
    },
    bulkDisable: {
      type: Boolean,
      default: false,
    },
    hasPermissions: {
      type: Boolean,
      default: true,
    },
    columnCondition: {
      type: String,
      default: '',
    },
    massiveActionsOptions: {
      type: Array,
      default: () => [],
    },
    height: {
      type: String,
      default: undefined,
    },
    emptyText: {
      type: String,
      default: undefined,
    },
    rowKey: {
      type: String,
      default: undefined,
    },
    treeProps: {
      type: Object,
      default: undefined,
    },
    rowClassName: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      multipleSelection: [],
      multipleReportSelection: [],
      toggleChecked: undefined,
      massiveActionsValue: '',
      SAMPLE_STATUSES,
      SAMPLE_REPORTS_STATUSES,
    };
  },
  computed: {
    ...mapGetters([
      'tablesStatus',
    ]),
    sorting() {
      return this.tablesStatus[this.tableKey].sorting;
    },
    currentPage() {
      return this.tablesStatus[this.tableKey].page;
    },
    perPage() {
      return this.tablesStatus[this.tableKey].perPage;
    },
    count() {
      return this.tablesStatus[this.tableKey].count;
    },
    tableRows() {
      return this.tablesStatus[this.tableKey].rows;
    },
    columns() {
      return Object.keys(this.tableColumns).reduce((columns, key) => {
        if (acl.userCanUse(this.tableColumns[key].permissions, true)
          && this.checkColumnCondition(this.tableColumns[key])) {
          return { ...columns, [key]: this.tableColumns[key] };
        }
        return columns;
      }, {});
    },
  },
  created() {
    // check if use permissions
    // if (this.hasPermissions) {
    // filter columns by permissions
    // this.columns = Object.keys(this.tableColumns).reduce((columns, key) => {
    //   if (acl.userCanUse(this.tableColumns[key].permissions)
    //     && this.checkColumnCondition(this.tableColumns[key])) {
    //     return { ...columns, [key]: this.tableColumns[key] };
    //   }
    //   return columns;
    // }, {});
    // }
    // else {
    //   this.columns = { ...this.tableColumns };
    // }
  },
  mounted() {
    // TODO logics to retrieve filters from url
    this.$emit('reload');
    this.$nextTick(() => {
      const table = this.$refs.multipleTable;
      if (table && table.$el) {
        const resizeObserver = new MutationObserver(() => {
          const ths = table.$el.querySelectorAll('th.el-table__cell');
          ths.forEach((thElement) => {
            const currentWidth = thElement.offsetWidth;
            if (currentWidth < 120) {
              thElement.style.setProperty('width', '50px');
              thElement.style.setProperty('min-width', '50px');
            }
          });
        });

        resizeObserver.observe(table.$el, {
          attributes: true,
          subtree: true,
          attributeFilter: ['style'],
        });

        this.$once('hook:beforeDestroy', () => {
          resizeObserver.disconnect();
        });
      }
    });
  },
  methods: {
    ...mapActions([
      'updateTableSorting',
      'updateTablePerPage',
      'updateTableCurrentPage',
      'updateColumnSort',
    ]),
    isReportRowSelected(row) {
      return (row.reports || []).every(report => this.multipleReportSelection.includes(`${report.sampleId}_${report.id}`));
    },
    toggleSelectAllReports() {
      const allVisibleReportIds = this.tableRows.flatMap(row => (row.reports || []).map(report => `${report.sampleId}_${report.id}`));
      if (allVisibleReportIds.length === 0) return;
      let newSelection = [...this.multipleReportSelection];
      if (!this.toggleChecked) {
        // Add all visible reports that aren't already selected
        allVisibleReportIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
      } else {
        newSelection = newSelection.filter(id => !allVisibleReportIds.includes(id));
      }
      this.toggleChecked = !this.toggleChecked;
      this.multipleReportSelection = newSelection;
      this.$emit('reportSelectionChange', this.multipleReportSelection);
    },
    collapseItemLabelKey(column, item) {
      return column.collapseItemLabel
        ? column.collapseItemLabel(item)
        : `${item[column.collapseValue]}_${item[column.collapseAddon]}`;
    },
    checkColumnCondition(column) {
      return !column.columnCondition
        || (column.columnCondition && column.columnCondition === this.columnCondition);
    },
    tableCellClassName({ row, column }) {
      if (this.columns[column.property] && this.columns[column.property].customClass) {
        const modifier = this.columns[column.property].getClassModifier
          && `table-cell-${this.columns[column.property].key}--${this.columns[column.property].getClassModifier(row[column.property])}`;
        return `table-cell-${this.columns[column.property].key} ${modifier || ''}`;
      }
      return '';
    },
    handleSelectionChange(val) {
      this.multipleSelection = val;
      this.$emit('selectionChange', val);
    },
    handleReportSelectionChange(row, checked) {
      if (!row.reports) return;
      const reportIds = row.reports.map(report => `${report.sampleId}_${report.id}`);
      let newSelection = [...this.multipleReportSelection];
      if (checked) {
        reportIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
      } else {
        // Remove reports
        newSelection = newSelection.filter(id => !reportIds.includes(id));
      }
      this.multipleReportSelection = newSelection;
      this.$emit('reportSelectionChange', this.multipleReportSelection);
    },
    sortChange(sorting) {
      const { column, ...sortData } = sorting;
      this.$router.replace({
        query: {
          ...this.$route.query,
          tableKey: this.tableKey,
          sorting: JSON.stringify({ ...sortData, prop: this.columns[sortData.prop].key }),
        },
      }).catch(() => {});
    },
    handleSizeChange(perPage) {
      // this.updateTablePerPage({ tableKey: this.tableKey, perPage });
      // this.updateTableCurrentPage({ tableKey: this.tableKey, page: 1 });
      // this.$emit('reload');
      this.$router.replace({
        query: {
          ...this.$route.query,
          tableKey: this.tableKey,
          perPage,
          page: 1,
        },
      }).catch(() => {});
    },
    handleCurrentChange(page) {
      // this.updateTableCurrentPage({ tableKey: this.tableKey, page });
      // this.$emit('reload');
      this.$router.replace({
        query: {
          ...this.$route.query,
          tableKey: this.tableKey,
          page,
        },
      }).catch(() => {});
    },
    propertyValue(object, path) {
      return lodashGet(object, path, '');
    },
    tableRowClassName({ row }) {
      return this.rowClassName(row);
    },
  },
};
</script>

<style lang="scss">

.table-wrapper {
  .el-table {
    th.el-table__cell {
      padding: 6px 0;

      .cell {
        white-space: normal !important;
        word-break: keep-all !important;
        line-height: 1.4;
        overflow: visible !important; // Permette al contenuto di essere visibile
      }
    }

    // Forza la larghezza minima via CSS
    colgroup col {
      min-width: 50px !important;
    }
  }

    .el-pagination.pagination {
      display: flex;
      justify-content: flex-end;
      padding-top: 1rem;
    }

    .select-all-rdp {
      cursor: pointer;
    }

    .massive-actions {
      display: flex;
      flex-direction: column;

      &__label {
        color: $--color-primary;
        font-size: $--form-label-font-size;
        line-height: 1;
        margin-top: 1.5rem;
        margin-bottom: 0.5rem;
      }

      &__select {
        display: flex;

        .el-button {
          margin-left: .5rem;
        }
      }
    }

    .state-icon {
      font-size: 1.2rem;
      &.fa-check-circle {
        color: $table-action-green;
      }
      &.fa-do-not-enter {
        color: $table-action-red-light;
      }
    }

    .massive-action-disable {
      margin-top: 1rem;
      .disable-selected {
        margin-top: 1rem;
      }
    }

    .slot-collapse-list {
      a {
        text-decoration: none;
      }
      .el-collapse,
      .el-collapse-item__header,
      .el-collapse-item__content
      {
        border: none;
        background: transparent;
        &:hover {
          background: initial;
        }
      }
      .el-collapse-item__header {
        max-height: 2rem;
      }
      .el-collapse-item__content {
        padding-left: 0.25rem;
        // padding-top: 1rem;
        ul {
          padding: 0;
          margin: 0;
          li {
            display: inline-block;
            margin: 0 0.1rem;
          }
        }
      }
    }

  }

</style>
